import { useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, X, MessageSquare, Phone, Send, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/data";
import { metaLeadFields, trackLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEL = "tel:+13149361837";
const SMS = "sms:+13149361837";

export const ContactWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Hide on admin / utility routes
  if (/^\/(admin|sign|complete|drivers|pay|save-card|review|receipt)/.test(location.pathname)) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    setLoading(true);
    const leadFields = metaLeadFields();
    try {
      await axios.post(`${API}/contact`, { ...form, ...leadFields });
      trackLead(leadFields.meta_event_id);
      toast.success("Thanks! We'll reach out shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
      setOpen(false);
    } catch {
      toast.error("Couldn't send. Please call or text us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-[#F2B705] transition-colors text-sm";

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end" data-testid="contact-widget">
      {open && (
        <div className="mb-3 w-[min(92vw,360px)] rounded-2xl overflow-hidden bg-[#0A0A0A] border border-white/10 shadow-2xl shadow-black/60 animate-in fade-in slide-in-from-bottom-4 duration-200" data-testid="contact-widget-panel">
          <div className="bg-[#F2B705] text-black px-5 py-4 flex items-start justify-between">
            <div>
              <div className="font-display font-extrabold text-xl leading-tight">Talk to us directly</div>
              <div className="text-sm opacity-80">We usually reply within minutes.</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="contact-widget-close-panel" className="shrink-0 -mr-1 -mt-1 p-1 hover:opacity-70"><X size={22} /></button>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-2 gap-3">
              <a href={SMS} data-testid="contact-widget-text" className="flex flex-col items-center gap-1.5 bg-[#111] border border-white/10 rounded-xl py-4 hover:border-[#F2B705]/60 transition-colors">
                <MessageSquare className="text-[#F2B705]" size={24} />
                <span className="font-bold text-white text-sm">Text Us</span>
              </a>
              <a href={TEL} data-testid="contact-widget-call" className="flex flex-col items-center gap-1.5 bg-[#111] border border-white/10 rounded-xl py-4 hover:border-[#F2B705]/60 transition-colors">
                <Phone className="text-[#F2B705]" size={24} />
                <span className="font-bold text-white text-sm">Call Us</span>
              </a>
            </div>
            <a href={TEL} data-testid="contact-widget-number" className="block text-center text-white/70 underline underline-offset-4 mt-4 hover:text-[#F2B705] transition-colors">{COMPANY.phone}</a>

            <div className="flex items-center gap-3 my-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-white/40 text-xs">or leave your info</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="contact-widget-form">
              <input className={inputCls} placeholder="Your name *" data-testid="contact-widget-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              <input className={inputCls} type="tel" placeholder="Phone number *" data-testid="contact-widget-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              <input className={inputCls} type="email" placeholder="Email address *" data-testid="contact-widget-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              <textarea className={inputCls} rows={2} placeholder="How can we help? (optional)" data-testid="contact-widget-message" value={form.message} onChange={(e) => set("message", e.target.value)} />
              <button type="submit" disabled={loading} data-testid="contact-widget-submit" className="w-full bg-[#F2B705] text-black font-bold py-3 rounded-xl hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Send Message
              </button>
            </form>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="contact-widget-launcher"
        aria-label="Contact us"
        className="w-[60px] h-[60px] rounded-full bg-[#F2B705] text-black flex items-center justify-center shadow-lg shadow-black/40 hover:scale-105 active:scale-95 transition-transform"
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </button>
    </div>
  );
};
